import {
  Badge, Button, Card, DataTable, DateInput, ErrorState, Field, Grid, Input, Notice, PageHeader,
  Permissions, Row, Select, Stack, Stat, usePermission,
  useApi, useI18n, usePrintedFile,
  type ChildSummaryResponse, type InvoiceResponse, type PaymentMethod, type PaymentResponse,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { ChildPicker } from '../components/ChildPicker';
import { YearFees } from './YearFees';

type Tab = 'yearFees' | 'overdue' | 'payments' | 'income' | 'invoices';

const TABS: Record<Tab, string> = {
  yearFees: 'yearFees.title',
  overdue: 'finance.overdue',
  payments: 'finance.tab.payments',
  income: 'finance.income',
  invoices: 'invoices.title',
};

const METHODS: PaymentMethod[] = ['Cash', 'BankTransfer', 'Cheque', 'Other'];

/**
 * Financial administration (§4.8.5).
 *
 * What a nursery does with money: set each child's total for the year and see who still owes (Q3, Q44), chase
 * what is late, record the installments that come in, and see what a period added up to. They are tabs rather
 * than screens because an accountant moves between them constantly and each is a table.
 *
 * v1 offers no fee plans, discounts, extra charges, proration or monthly generation (Q15). The API keeps them
 * as the engine underneath; the invoices tab only lists what exists.
 *
 * Nothing here is reachable without `finance.view`, and the hard constraints mean teaching and front-desk
 * roles can never hold it however the tenant configures things (FR-STF-11).
 */
export function Finance() {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('yearFees');

  return (
    <Stack>
      <PageHeader title={t('nav.finance')} />

      <Row>
        {(Object.keys(TABS) as Tab[]).map((value) => (
          <Button
            key={value}
            variant={tab === value ? 'primary' : 'ghost'}
            onClick={() => setTab(value)}
          >
            {t(TABS[value])}
          </Button>
        ))}
      </Row>

      {tab === 'yearFees' && <YearFees />}
      {tab === 'overdue' && <Overdue />}
      {tab === 'invoices' && <Invoices />}
      {tab === 'payments' && <Payments />}
      {tab === 'income' && <Income />}
    </Stack>
  );
}

/** The overdue list (FR-FIN-16), with the bulk reminder FR-FIN-19 asks for. */
function Overdue() {
  const api = useApi();
  const { t, formatMoney, formatDate, formatPhone, formatNumber } = useI18n();
  const [sort, setSort] = useState<'amount' | 'days'>('amount');
  const canRemind = usePermission(Permissions.finance.sendReminder);

  const queryClient = useQueryClient();
  const overdue = useQuery({
    queryKey: ['finance', 'overdue', sort],
    queryFn: () => api.finance.overdue({ sort }),
  });

  const remind = useMutation({
    mutationFn: (childIds: number[] | null) => api.finance.sendReminders(childIds),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['finance', 'overdue'] }),
  });

  return (
    <Stack>
      <Row between>
        <Select
          aria-label={t('action.filter')}
          value={sort}
          onChange={(event) => setSort(event.target.value as 'amount' | 'days')}
        >
          <option value="amount">{t('invoices.outstanding')}</option>
          <option value="days">{t('invoices.daysOverdue', { days: '' }).trim()}</option>
        </Select>

        {canRemind && (
          <Button
            busy={remind.isPending}
            busyLabel={t('action.saving')}
            onClick={() => remind.mutate(null)}
          >
            {t('finance.sendReminders')}
          </Button>
        )}
      </Row>

      {remind.isError && <ErrorState error={remind.error} />}
      {remind.data && (
        <Notice tone="success">
          {t('finance.remindersSent', {
            sent: formatNumber(remind.data.sent),
            skipped: formatNumber(remind.data.skipped),
          })}
        </Notice>
      )}

      {overdue.data && (
        <Notice tone={overdue.data.rows.length > 0 ? 'warning' : 'success'}>
          {t('finance.overdueTotal', {
            amount: formatMoney(overdue.data.totalOutstandingMinorUnits, overdue.data.currency),
          })}
        </Notice>
      )}

      <Card flush>
        <DataTable
          caption={t('finance.overdue')}
          rows={overdue.data?.rows}
          rowKey={(row) => row.childId}
          isLoading={overdue.isPending}
          error={overdue.error}
          onRetry={() => void overdue.refetch()}
          empty={t('invoices.empty')}
          columns={[
            { key: 'child', header: t('child.one'), render: (r) => r.childName },
            { key: 'class', header: t('class.one'), render: (r) => r.className ?? '—' },
            {
              key: 'outstanding',
              header: t('invoices.outstanding'),
              numeric: true,
              render: (r) => formatMoney(r.outstandingMinorUnits, r.currency),
            },
            {
              key: 'days',
              header: t('invoices.dueDate'),
              numeric: true,
              render: (r) => (
                <Badge tone={r.daysOverdue > 30 ? 'danger' : 'warning'}>
                  {t('invoices.daysOverdue', { days: formatNumber(r.daysOverdue) })}
                </Badge>
              ),
            },
            { key: 'since', header: t('finance.from'), render: (r) => formatDate(r.oldestDueDate) },
            {
              key: 'guardian',
              header: t('guardians.primary'),
              render: (r) => (
                <span className="dg-stack dg-stack--tight">
                  <span>{r.primaryGuardianName ?? '—'}</span>
                  {/* The number is the point of this column: it is how the call actually gets made. */}
                  <span className="dg-muted dg-text-sm">{formatPhone(r.primaryGuardianPhone)}</span>
                </span>
              ),
            },
            {
              key: 'reminded',
              header: t('finance.sendReminders'),
              render: (r) => r.lastReminderAt ? formatDate(r.lastReminderAt) : '—',
            },
          ]}
        />
      </Card>
    </Stack>
  );
}

/**
 * Every invoice, the year totals among them, with their history: a changed total shows its voided predecessor.
 * Read only; totals are set on the year-fees tab, and v1 generates no monthly invoices (Q15).
 */
function Invoices() {
  const api = useApi();
  const { t, formatMoney, formatDate } = useI18n();
  const canVoid = usePermission(Permissions.finance.issueInvoice);

  const invoices = useQuery({
    queryKey: ['finance', 'invoices'],
    queryFn: () => api.finance.invoices({ pageSize: 50 }),
  });

  return (
    <Stack>
      <Card flush>
        <DataTable
          caption={t('invoices.title')}
          rows={invoices.data?.items}
          rowKey={(invoice) => invoice.id}
          isLoading={invoices.isPending}
          error={invoices.error}
          onRetry={() => void invoices.refetch()}
          empty={t('invoices.empty')}
          columns={[
            { key: 'number', header: t('invoices.number'), render: (i) => i.number },
            { key: 'child', header: t('child.one'), render: (i) => i.childName },
            {
              key: 'period',
              header: t('invoices.period'),
              render: (i) => `${formatDate(i.periodStart)} – ${formatDate(i.periodEnd)}`,
            },
            { key: 'due', header: t('invoices.dueDate'), render: (i) => formatDate(i.dueDate) },
            {
              key: 'total',
              header: t('invoices.total'),
              numeric: true,
              render: (i) => (
                <Amount text={formatMoney(i.totalMinorUnits, i.currency)} voidedAt={i.voidedAt} reason={i.voidReason} />
              ),
            },
            {
              key: 'outstanding',
              header: t('invoices.outstanding'),
              numeric: true,
              render: (i) => formatMoney(i.outstandingMinorUnits, i.currency),
            },
            {
              key: 'status',
              header: t('audit.action'),
              render: (i) => (
                <Badge tone={i.status === 'Paid' ? 'success'
                  : i.status === 'Overdue' ? 'danger'
                  : i.status === 'Draft' ? 'neutral' : 'info'}>
                  {t(`status.${i.status}`)}
                </Badge>
              ),
            },
            ...(canVoid ? [{
              key: 'void',
              header: '',
              render: (i: InvoiceResponse) => i.status === 'Void' ? null : (
                <VoidAction
                  label={t('finance.void.invoiceOf', { number: i.number })}
                  hint={t('finance.void.invoiceHint')}
                  onVoid={(reason) => api.finance.voidInvoice(i.id, reason)}
                />
              ),
            }] : []),
          ]}
        />
      </Card>
    </Stack>
  );
}

/** Recording an installment (FR-FIN-09), who paid it (Q31), and the history behind it. */
function Payments() {
  const api = useApi();
  const { t, formatMoney, formatDate, formatPhone, toMinorUnits } = useI18n();
  const canRecord = usePermission(Permissions.finance.recordPayment);
  const queryClient = useQueryClient();

  const [child, setChild] = useState<ChildSummaryResponse | null>(null);
  const [childUnchosen, setChildUnchosen] = useState(false);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('Cash');
  const [reference, setReference] = useState('');
  const [paidBy, setPaidBy] = useState<number | ''>('');

  // Who paid is one of the child's own guardians, never typed: the account stays the child's (Q31).
  const guardians = useQuery({
    queryKey: ['guardians', child?.id],
    queryFn: () => api.guardians.forChild(child!.id),
    enabled: child !== null,
  });

  const payments = useQuery({
    queryKey: ['finance', 'payments'],
    queryFn: () => api.finance.payments({ pageSize: 50 }),
  });

  const record = useMutation({
    mutationFn: () => api.finance.recordPayment({
      childId: child!.id,
      // Minor units, never a decimal. The exponent comes from the same function the display divides by,
      // because SYP is written in whole pounds: a hard-coded hundred here would record a hundred times
      // what the accountant typed and then render it back as the inflated figure.
      amountMinorUnits: toMinorUnits(Number(amount)),
      paidAt: null,
      method,
      reference: reference || null,
      note: null,
      invoiceIds: null,
      paidByGuardianMembershipId: paidBy === '' ? null : paidBy,
    }),
    onSuccess: async () => {
      setAmount('');
      setReference('');
      setPaidBy('');
      await queryClient.invalidateQueries({ queryKey: ['finance'] });
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    // Text typed without a match taken is not a child: say so rather than send nothing.
    if (child === null) setChildUnchosen(true);
    else if (Number(amount) > 0) record.mutate();
  };

  return (
    <Stack>
      {canRecord && (
        <Card title={t('payments.record')}>
          <form onSubmit={submit}>
            <Stack>
              {record.isError && <ErrorState error={record.error} />}
              {record.isSuccess && <Notice tone="success">{t('payments.recorded')}</Notice>}

              <Field label={t('child.one')} required error={childUnchosen ? t('childPicker.unchosen') : null}>
                {(props) => (
                  <ChildPicker field={props}
                    value={child}
                    onChange={(next) => {
                      setChild(next);
                      setChildUnchosen(false);
                      setPaidBy('');
                    }}
                  />
                )}
              </Field>

              <Field label={t('payments.amount')} required>
                {(props) => (
                  <Input {...props}
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                  />
                )}
              </Field>

              <Field label={t('payments.paidBy')}>
                {(props) => (
                  <Select {...props}
                    value={paidBy}
                    disabled={child === null}
                    onChange={(event) => setPaidBy(event.target.value === '' ? '' : Number(event.target.value))}
                  >
                    <option value="">{t('payments.paidBy.none')}</option>
                    {(guardians.data ?? []).map((guardian) => (
                      <option key={guardian.guardianMembershipId} value={guardian.guardianMembershipId}>
                        {guardian.fullName ?? formatPhone(guardian.phone)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field label={t('payments.method')}>
                {(props) => (
                  <Select {...props} value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
                    {METHODS.map((value) => (
                      <option key={value} value={value}>{t(`payments.method.${value}`)}</option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field label={t('payments.reference')}>
                {(props) => (
                  <Input {...props} value={reference} onChange={(event) => setReference(event.target.value)} />
                )}
              </Field>

              <Row>
                <Button type="submit" busy={record.isPending} busyLabel={t('action.saving')}>
                  {t('payments.record')}
                </Button>
              </Row>
            </Stack>
          </form>
        </Card>
      )}

      <Card flush>
        <DataTable
          caption={t('payments.history')}
          rows={payments.data?.items}
          rowKey={(payment) => payment.id}
          isLoading={payments.isPending}
          error={payments.error}
          onRetry={() => void payments.refetch()}
          empty={t('payments.noPayments')}
          columns={[
            { key: 'date', header: t('payments.date'), render: (p) => formatDate(p.paidAt) },
            { key: 'child', header: t('child.one'), render: (p) => p.childName },
            {
              key: 'amount',
              header: t('payments.amount'),
              numeric: true,
              render: (p) => (
                <Amount text={formatMoney(p.amountMinorUnits, p.currency)} voidedAt={p.voidedAt} reason={p.voidReason} />
              ),
            },
            { key: 'method', header: t('payments.method'), render: (p) => t(`payments.method.${p.method}`) },
            { key: 'paidBy', header: t('payments.paidBy'), render: (p) => p.paidByName ?? '—' },
            {
              key: 'unallocated',
              header: t('payments.credit'),
              numeric: true,
              render: (p) => p.unallocatedMinorUnits > 0
                ? formatMoney(p.unallocatedMinorUnits, p.currency)
                : '—',
            },
            {
              key: 'status',
              header: t('audit.action'),
              render: (p) => p.voidedAt
                ? <Badge tone="neutral">{t('status.Void')}</Badge>
                : <Badge tone="success">{t('status.Paid')}</Badge>,
            },
            ...(canRecord ? [{
              key: 'void',
              header: '',
              render: (p: PaymentResponse) => p.voidedAt ? null : (
                <VoidAction
                  label={t('finance.void.paymentOf', { name: p.childName })}
                  hint={t('finance.void.paymentHint')}
                  onVoid={(reason) => api.finance.voidPayment(p.id, reason)}
                />
              ),
            }] : []),
          ]}
        />
      </Card>
    </Stack>
  );
}

/** The monthly income report (FR-FIN-17), with any custom range (FR-FIN-18). */
function Income() {
  const api = useApi();
  const { t, formatMoney, formatNumber, today } = useI18n();
  // The printed report is a report (FR-ADM-20): `reports.view` beside the `finance.view` this tab already needs.
  const canPrint = usePermission(Permissions.tenant.viewReports);
  const printed = usePrintedFile();

  const [from, setFrom] = useState(() => `${today().slice(0, 7)}-01`);
  const [to, setTo] = useState(today);
  const fileKey = `income-${from}-${to}`;

  const income = useQuery({
    queryKey: ['finance', 'income', from, to],
    queryFn: () => api.finance.income({ from, to }),
  });

  return (
    <Stack>
      <Row>
        <Field label={t('finance.from')}>
          {(props) => (
            <DateInput {...props} value={from} onChange={setFrom} />
          )}
        </Field>
        <Field label={t('finance.to')}>
          {(props) => (
            <DateInput {...props} value={to} onChange={setTo} />
          )}
        </Field>
      </Row>

      {canPrint && (
        <Row>
          <Button
            variant="secondary"
            disabled={printed.busy === fileKey}
            onClick={() => void printed.open(fileKey, () => api.reports.incomePdf({ from, to }))}
          >
            {t(printed.busy === fileKey ? 'action.printing' : 'finance.printIncome')}
          </Button>
        </Row>
      )}
      {printed.error != null && <ErrorState error={printed.error} />}

      {income.isError && <ErrorState error={income.error} onRetry={() => void income.refetch()} />}

      {income.data && (
        <>
          <Grid>
            <Stat
              label={t('finance.incomeTotal')}
              value={formatMoney(income.data.totalMinorUnits, income.data.currency)}
            />
            <Stat
              label={t('finance.previousPeriod')}
              value={formatMoney(income.data.previousTotalMinorUnits, income.data.currency)}
            />
            <Stat
              label={t('invoices.outstanding')}
              value={formatMoney(income.data.outstandingMinorUnits, income.data.currency)}
              tone="warning"
            />
          </Grid>

          <Card title={t('finance.byClass')} flush>
            <DataTable
              caption={t('finance.byClass')}
              rows={income.data.byClass}
              rowKey={(row) => row.key}
              empty={t('state.empty')}
              columns={[
                { key: 'key', header: t('class.one'), render: (r) => r.key },
                {
                  key: 'amount',
                  header: t('finance.incomeTotal'),
                  numeric: true,
                  render: (r) => formatMoney(r.amountMinorUnits, income.data.currency),
                },
                { key: 'count', header: '#', numeric: true, render: (r) => formatNumber(r.count) },
              ]}
            />
          </Card>

          <Card title={t('finance.byMethod')} flush>
            <DataTable
              caption={t('finance.byMethod')}
              rows={income.data.byMethod}
              rowKey={(row) => row.key}
              empty={t('state.empty')}
              columns={[
                {
                  key: 'key',
                  header: t('payments.method'),
                  render: (r) => t(`payments.method.${r.key}`),
                },
                {
                  key: 'amount',
                  header: t('finance.incomeTotal'),
                  numeric: true,
                  render: (r) => formatMoney(r.amountMinorUnits, income.data.currency),
                },
                { key: 'count', header: '#', numeric: true, render: (r) => formatNumber(r.count) },
              ]}
            />
          </Card>
        </>
      )}
    </Stack>
  );
}

/**
 * Voiding a payment or an invoice, which is a correction, never an edit (Q3, Q44): the row stays, struck through,
 * with the reason the trail keeps. A voided payment's allocations are reversed; a voided invoice's payments become
 * credit for whatever is issued in its place. Two steps, the reason required, so a slip voids nothing.
 */
export function VoidAction({ label, hint, onVoid }: {
  label: string; hint: string; onVoid: (reason: string) => Promise<void>;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');

  const voiding = useMutation({
    mutationFn: () => onVoid(reason.trim()),
    onSuccess: async () => {
      setAsking(false);
      setReason('');
      await queryClient.invalidateQueries({ queryKey: ['finance'] });
    },
  });

  if (!asking) {
    return <Button variant="ghost" aria-label={label} onClick={() => setAsking(true)}>{t('invoices.void')}</Button>;
  }

  return (
    <Stack tight>
      <span className="dg-card__hint">{hint}</span>
      <Input
        aria-label={t('finance.void.reason')}
        placeholder={t('finance.void.reason')}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        autoFocus
      />
      {voiding.isError && <ErrorState error={voiding.error} />}
      <Row>
        <Button variant="danger" disabled={!reason.trim()} busy={voiding.isPending} onClick={() => voiding.mutate()}>
          {t('finance.void.confirm')}
        </Button>
        <Button variant="ghost" onClick={() => setAsking(false)}>{t('action.cancel')}</Button>
      </Row>
    </Stack>
  );
}

/** An amount as it was, struck through once voided, with the reason beside it. */
function Amount({ text, voidedAt, reason }: { text: string; voidedAt: string | null; reason: string | null }) {
  const { t } = useI18n();
  if (!voidedAt) return <>{text}</>;
  return (
    <span className="dg-stack dg-stack--tight">
      <s>{text}</s>
      {reason && <span className="dg-card__hint">{t('finance.voided', { reason })}</span>}
    </span>
  );
}
